import React, { useEffect, useState, useRef, Component } from 'react';
import { MapPin, Loader2, X } from 'lucide-react';
import { Input } from './ui/Input';
export interface AddressParts {
  /** Full formatted one-line address (what gets stored as the drop address). */
  label: string;
  /** Street + house number, e.g. "Drottninggatan 56". */
  street?: string;
  city?: string;
  postalCode?: string;
  lat?: number;
  lon?: number;
}
interface AddressAutocompleteProps {
  value: string;
  /** Fired on every keystroke with the raw text (keeps the field controlled). */
  onChange: (value: string) => void;
  /** Fired when the user picks a suggestion — gives structured parts. */
  onSelect?: (parts: AddressParts) => void;
  placeholder?: string;
  /** Tailwind classes for the input (so it matches the dark admin surfaces). */
  className?: string;
  /** Classes for the suggestions dropdown panel (dark by default). */
  panelClassName?: string;
  id?: string;
}
interface PhotonFeature {
  properties: {
    name?: string;
    street?: string;
    housenumber?: string;
    postcode?: string;
    city?: string;
    town?: string;
    village?: string;
    state?: string;
    country?: string;
  };
  geometry?: {
    coordinates?: [number, number];
  };
}
/** Build a clean one-line label + parts from a Photon GeoJSON feature. */
function featureToParts(f: PhotonFeature): AddressParts {
  const p = f.properties || {};
  const street = [p.street || p.name, p.housenumber].filter(Boolean).join(' ');
  const city = p.city || p.town || p.village || '';
  const label = [street || p.name, city, p.postcode, p.country].filter(Boolean).join(', ');
  const coords = f.geometry?.coordinates;
  return {
    label,
    street: street || p.name || '',
    city,
    postalCode: p.postcode || '',
    lat: coords ? coords[1] : undefined,
    lon: coords ? coords[0] : undefined
  };
}
/**
 * Address autocomplete using Photon (https://photon.komoot.io) — an OpenStreetMap
 * geocoder that needs NO API key and allows browser CORS, so it works as a
 * keyless, drop-in alternative to Google Places for picking a correct address.
 *
 * Debounced; closes on outside click / Escape; fully keyboard accessible
 * (arrow keys + Enter). Stores the formatted address back through onChange and
 * surfaces structured parts (street/city/postcode) through onSelect.
 */
export function AddressAutocomplete({
  value,
  onChange,
  onSelect,
  placeholder = 'Start typing an address…',
  className = 'bg-gray-800 border-gray-700 text-white',
  panelClassName = 'bg-gray-900 border-gray-700',
  id
}: AddressAutocompleteProps) {
  const [suggestions, setSuggestions] = useState<AddressParts[]>([]);
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [activeIndex, setActiveIndex] = useState(-1);
  const wrapperRef = useRef<HTMLDivElement | null>(null);
  // Suppresses a refetch immediately after the user picks a suggestion.
  const justSelected = useRef(false);
  // Debounced lookup whenever `value` changes.
  useEffect(() => {
    if (justSelected.current) {
      justSelected.current = false;
      return;
    }
    const q = value.trim();
    if (q.length < 3) {
      setSuggestions([]);
      setOpen(false);
      return;
    }
    let cancelled = false;
    const handle = window.setTimeout(async () => {
      setLoading(true);
      try {
        const res = await fetch(`https://photon.komoot.io/api/?q=${encodeURIComponent(q)}&limit=6&lang=en`);
        const data = await res.json();
        if (cancelled) return;
        const feats: PhotonFeature[] = Array.isArray(data?.features) ? data.features : [];
        const parts = feats.map(featureToParts).filter(p => p.label && p.label.length > 0);
        setSuggestions(parts);
        setOpen(parts.length > 0);
        setActiveIndex(-1);
      } catch {
        if (!cancelled) {
          setSuggestions([]);
          setOpen(false);
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    }, 350);
    return () => {
      cancelled = true;
      window.clearTimeout(handle);
    };
  }, [value]);
  // Close on outside click.
  useEffect(() => {
    const onDocClick = (e: MouseEvent) => {
      if (wrapperRef.current && !wrapperRef.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener('mousedown', onDocClick);
    return () => document.removeEventListener('mousedown', onDocClick);
  }, []);
  const choose = (parts: AddressParts) => {
    justSelected.current = true;
    onChange(parts.label);
    onSelect?.(parts);
    setOpen(false);
    setSuggestions([]);
    setActiveIndex(-1);
  };
  const onKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (!open || suggestions.length === 0) return;
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setActiveIndex(i => (i + 1) % suggestions.length);
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setActiveIndex(i => i <= 0 ? suggestions.length - 1 : i - 1);
    } else if (e.key === 'Enter' && activeIndex >= 0) {
      e.preventDefault();
      choose(suggestions[activeIndex]);
    } else if (e.key === 'Escape') {
      setOpen(false);
    }
  };
  return <div ref={wrapperRef} className="relative" data-id="element-15" data-mp="jbi5jlg22506">
      <div className="relative" data-id="element-16" data-mp="nbsdh3ner5ho">
        <MapPin className="w-4 h-4 text-gray-500 absolute left-3 top-1/2 -translate-y-1/2 pointer-events-none" data-id="element-17" data-mp="ncu0m6hp7ifr" />
        <Input id={id} value={value} onChange={e => onChange(e.target.value)} onFocus={() => suggestions.length > 0 && setOpen(true)} onKeyDown={onKeyDown} placeholder={placeholder} autoComplete="off" role="combobox" aria-expanded={open} aria-autocomplete="list" className={`pl-9 pr-9 ${className}`} data-id="element-18" data-mp="1b2oaqgamuswb" />
        {loading ? <Loader2 className="w-4 h-4 text-gray-500 absolute right-3 top-1/2 -translate-y-1/2 animate-spin" data-id="element-19" data-mp="3rq0l088bsmtt" /> : value ? <button type="button" onClick={() => {
        onChange('');
        setSuggestions([]);
        setOpen(false);
      }} aria-label="Clear address" className="absolute right-2.5 top-1/2 -translate-y-1/2 text-gray-500 hover:text-gray-300" data-id="element-20" data-mp="1xa1u8p44lusj">
            <X className="w-4 h-4" data-id="element-21" data-mp="1jhvps0ac5c8x" />
          </button> : null}
      </div>

      {open && suggestions.length > 0 && <ul role="listbox" className={`absolute z-30 mt-1 w-full max-h-64 overflow-y-auto rounded-xl border shadow-2xl ${panelClassName}`} data-id="element-22" data-mp="1egxzf0f69bwc">
          {suggestions.map((s, i) => <li key={`${s.label}-${i}`} role="option" aria-selected={i === activeIndex} data-id="element-23" data-mp="10jj6hlppmlzm" data-mpi={i}>
              <button type="button" onMouseDown={e => {
          e.preventDefault();
          choose(s);
        }} onMouseEnter={() => setActiveIndex(i)} className={`w-full text-left px-3 py-2.5 flex items-start gap-2.5 transition-colors ${i === activeIndex ? 'bg-brand-blue/20' : 'hover:bg-white/5'}`} data-id="element-24" data-mp="2yxujw7n4b7q6">
                <MapPin className="w-4 h-4 text-brand-blue mt-0.5 shrink-0" data-id="element-25" data-mp="qg4c7qqvh9rs" />
                <span className="text-sm text-gray-200 leading-snug" data-id="element-26" data-mp="ug4pv965dgxs">
                  {s.label}
                </span>
              </button>
            </li>)}
        </ul>}
    </div>;
}